<script setup lang="ts">
import { computed, nextTick, onMounted, ref, onUnmounted, watch } from "vue";

import type { ValueType } from "@/Common/Models/Ui/Input";
import type { iconName, iconSize } from "@/Modules/App/Models/Styles/Icons";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { isString, isValidString, capitalizeFirstLetter } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";

type TextareaProps = {
  disableCountError?: boolean;
  endIcon?: iconName;
  endIconSize?: iconSize;
  errorMessage?: string;
  extraMessage?: string;
  gutterBottom?: boolean;
  hasCounter?: boolean;
  inputClass?: string;
  inputId?: string;
  isDisabled?: boolean;
  isResizable?: boolean;
  label?: string;
  maxLength?: number;
  modelValue?: ValueType;
  placeholder?: string;
  rows?: number;
  startIcon?: iconName;
  startIconSize?: iconSize;
  ignoreEnter?: boolean;
  isHighlitedTitle?: boolean;
};

const LINE_HEIGHT_INPUT = 22;
const TOTAL_PADDING_Y = 24;

const props = withDefaults(defineProps<TextareaProps>(), {
  isDisabled: false,
  hasCounter: false,
  startIconSize: "md",
  endIconSize: "md",
  rows: 3,
  isResizable: false,
  gutterBottom: true,
  ignoreEnter: false,
  isHighlitedTitle: false,
});

const internalValue = ref<ValueType>(props.modelValue);
const textarea = ref<HTMLTextAreaElement | null>();
const textfield = ref<HTMLElement | null>();
const textareaHeight = ref<number>(-1);

const emit = defineEmits<{
  (event: "update:modelValue", value: ValueType): void;
  (event: "change", value: ValueType): void;
  (event: "blur"): void;
  (event: "focus"): void;
  (event: "onSubmit"): void;
  (event: "clickEndIcon"): void;
  (event: "clickStartIcon"): void;
}>();

const hasLabel = computed(() => isValidString(props.label));

const hasError = computed(() => isValidString(props.errorMessage));

const charCount = computed((): string | undefined => {
  if (props.hasCounter) {
    if (!hasValue(internalValue.value) || !isString(internalValue.value)) {
      return `${0}/${props.maxLength}`;
    }

    if (!hasValue(props.maxLength)) {
      return internalValue.value.length.toString();
    }

    if (props.maxLength < internalValue.value.length) {
      return `${props.maxLength - internalValue.value.length}/${props.maxLength}`;
    }

    return `${internalValue.value.length}/${props.maxLength}`;
  }

  return undefined;
});

const getInputValue = () => {
  if (hasValue(textarea.value)) {
    if (props.ignoreEnter) {
      return textarea.value.value.replaceAll("\n", " ");
    } else {
      return textarea.value.value;
    }
  } else {
    return "";
  }
};

const resize = () => {
  nextTick(() => {
    if (hasValue(textarea.value) && hasValue(textfield.value) && props.isResizable) {
      if (textarea.value.scrollHeight > textareaHeight.value) {
        // Caso cuando crece el textarea
        textarea.value.style.height = `${textarea.value.scrollHeight}px`;

        if (textfield.value.clientHeight < textarea.value.scrollHeight) {
          // Caso cuando el textfield tiene un max-height o alguna limitación de tamaño via css
          // Ponemos el textfield como el tamaño maximo que puede tener el textarea
          // y ponemos como máximo el textarea al alto que puede tener el textfield para evitar el overflow y comportamientos raros
          textfield.value.style.height = `${textarea.value.scrollHeight}px`;
          textareaHeight.value = textfield.value.clientHeight;
          textfield.value.style.height = "";
        } else {
          // Puede crecer sin límite
          textareaHeight.value = textarea.value.scrollHeight;
        }

        textarea.value.style.height = "";
      } else {
        // Caso cuando el textarea tiene que decrecer
        // Se pone el height a 1px para calcular el alto máximo del contenido y volver a limpiar el height
        textarea.value.style.height = "1px";
        textareaHeight.value = textarea.value.scrollHeight;
        textarea.value.style.height = "";
      }

      textarea.value.scrollTop = textarea.value.scrollHeight;
    }
  });
};

const onChange = () => {
  if (props.isDisabled) return;

  internalValue.value = getInputValue();

  emit("update:modelValue", internalValue.value);
  emit("change", internalValue.value);

  if (props.isResizable) resize();
};

const onSubmit = () => {
  emit("onSubmit");
};

const onBlur = () => {
  emit("blur");
};

const onFocus = () => emit("focus");

const onClickStartIcon = () => emit("clickStartIcon");

const onClickEndIcon = () => emit("clickEndIcon");

const reloadValue = () => {
  internalValue.value = props.modelValue;

  if (props.isResizable) {
    nextTick(() => {
      resize();
    });
  }
};

const deleteWatch = watch(() => props.modelValue, reloadValue);

onMounted(reloadValue);

onUnmounted(() => {
  deleteWatch();
});
</script>

<template>
  <div
    ref="textfield"
    class="textfield"
    :class="{ error: hasError, disabled: !!props.isDisabled, 'mb-4': props.gutterBottom }"
  >
    <label
      v-if="hasLabel"
      class="textfield__label"
      :class="{ 'textfield__label--highlited': props.isHighlitedTitle }"
    >
      {{ label }}
    </label>

    <div
      class="textfield__input"
      :class="{
        'textfield__input--start_icon': hasValue(props.startIcon),
        'textfield__input--end_icon': hasValue(props.endIcon),
        'textfield__input--resize': true,
      }"
      :style="{ height: props.isResizable && textareaHeight > 0 ? `${textareaHeight}px` : 'auto' }"
    >
      <TcIcon
        v-if="hasValue(props.startIcon)"
        class="textfield__input__start_icon"
        :size="props.startIconSize"
        :name="props.startIcon"
        @click="onClickStartIcon"
      />

      <textarea
        v-model="internalValue"
        ref="textarea"
        :id="props.inputId"
        :placeholder="capitalizeFirstLetter(placeholder)"
        :disabled="props.isDisabled"
        :rows="props.rows"
        :style="{
          'min-height': hasValue(props.rows)
            ? `calc((${LINE_HEIGHT_INPUT}px * ${props.rows}) + ${TOTAL_PADDING_Y}px)`
            : `inherit`,
          'max-height': hasValue(props.rows) ? `100%` : `inherit`,
        }"
        :maxlength="props.maxLength"
        @input="onChange"
        @keyup="onChange"
        @keyup.enter="onSubmit"
        @blur="onBlur"
        @focus="onFocus"
      />

      <TcIcon
        v-if="hasValue(props.endIcon)"
        :name="props.endIcon"
        class="textfield__input__end_icon"
        :color="hasError ? 'semantic-error' : undefined"
        :size="props.endIconSize"
        @click="onClickEndIcon"
      />
    </div>

    <div class="textfield__info" :class="{ error: hasError }">
      <div v-if="hasError || isValidString(props.extraMessage)" class="textfield__info__text">
        <span v-if="hasError" class="textfield__info__text-error">
          {{ errorMessage }}
        </span>

        <span v-else-if="isValidString(props.extraMessage)">
          {{ props.extraMessage }}
        </span>
      </div>

      <div v-if="props.hasCounter" class="textfield__info__counter" :class="{ error: hasError }">
        <span>
          {{ charCount }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.textfield {
  display: block;
  width: 100%;

  &__label {
    @include text.general(low, l);
    @include colors.color(content, high);
    @include sp.padding(b, 1);

    display: block;

    &--highlited {
      @include text.general(high, s);
    }
  }

  &__input {
    @include radius(s);
    @include text.general(low, l);
    @include colors.color(content, high);

    position: relative;
    width: 100%;
    min-height: calc($space-3 + $space-3 + 1.375rem); // line-height typography-low-l

    textarea {
      @include sp.padding(x, 4);
      @include sp.padding(y, 3);
      @include text.general(low, l);
      @include colors.color(content, high);
      @include border(1, solid);
      @include colors.border(content, low);

      display: block;
      resize: none;
      width: 100%;
      text-align: left;

      &::placeholder {
        @include colors.color(content, mid);
      }

      &::-webkit-date-and-time-value {
        text-align: left;
      }

      &:focus-visible {
        outline: none;
      }

      &:focus {
        @include border(1, solid);
        @include colors.border(content, mid);
      }
    }

    .textfield__input__end_icon {
      display: none;

      &.visible {
        display: block;
      }
    }

    textarea {
      min-height: 90px;
      max-height: fit-content;
      white-space: pre-line;
    }

    &--resize {
      textarea {
        @include utils.noScrollbar();

        & {
          height: 100%;
        }
      }
    }

    &__end_icon,
    &__start_icon {
      position: absolute;
      top: 50%;
      -ms-transform: translateY(-50%);
      transform: translateY(-50%);
      cursor: pointer;
    }

    &__start_icon {
      left: 16px;
    }

    &__end_icon {
      right: 16px;
    }

    &--start_icon {
      textarea {
        @include sp.padding(l, 10);
      }
    }

    &--end_icon {
      textarea {
        @include sp.padding(r, 10);
      }
    }
  }

  &__info {
    display: flex;
    align-items: center;

    &__text,
    &__counter {
      @include text.general(low, s);
      @include colors.color(content, mid);
      @include sp.padding(t, 1);

      display: block;
    }

    &__text {
      flex-grow: 1;

      &-error {
        @include colors.color(semantic, error);
      }
    }

    &__counter {
      @include text.modification(left);

      &.error {
        @include colors.color(semantic, error);
      }
    }

    &.error {
      @include colors.color(semantic, error);
    }
  }

  &.disabled {
    .textfield__input {
      @include border(1, solid);
      @include colors.border(content, low);

      cursor: not-allowed;

      textarea {
        @include colors.color(content, low);
      }
    }
  }

  &.error {
    .textfield__input textarea {
      @include border(1, solid);
      @include colors.border(semantic, error);
      @include colors.color(semantic, error);

      &:focus {
        @include colors.color(content, high);
      }

      &:focus-visible {
        outline-color: $color-semantic-error;
        outline-width: 0;
      }
    }
  }
}
</style>
